import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  FindOrderItemsFromChainSuppliersQuery,
  OrderItemApi,
  UpdateAmountDeliveredBody,
  UpdateAmountDeliveredReceivedBody,
  UpdateAmountReceivedBody,
  UpdateOrderItemSupplierQuantityBody,
} from 'api';

export const findOrderItem = createAsyncThunk(
  'orderItem/findOrderItem',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderItems = createAsyncThunk<
  any,
  {
    id: string;
    take?: number;
    page?: number;
    name?: string;
  },
  { rejectValue: { message: string } }
>('orderItem/findOrderItems', async (params, { rejectWithValue }) => {
  try {
    const response = await OrderItemApi.findOrderItems(params.id, {
      take: params.take,
      page: params.page,
      name: params?.name,
    });

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findOrderItemToExport = createAsyncThunk(
  'orderItem/findOrderItemToExport',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findToExport(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findConfirmAmountDelivered = createAsyncThunk<
  any,
  { id: string; take: number; page: number },
  { rejectValue: { message: string } }
>(
  'orderItem/findConfirmAmountDelivered',
  async (params, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findConfirmAmountDelivered(
        params.id,
        {
          take: params.take,
          page: params.page,
        }
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findConfirmAmountReceived = createAsyncThunk<
  any,
  { id: string; take: number; page: number },
  { rejectValue: { message: string } }
>(
  'orderItem/findConfirmAmountReceived',
  async (params, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findConfirmAmountReceived(params.id, {
        take: params.take,
        page: params.page,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findConfirmAmountReceivedOfCustomer = createAsyncThunk<
  any,
  { id: string; take: number; page: number },
  { rejectValue: { message: string } }
>(
  'orderItem/findConfirmAmountReceivedOfCustomer',
  async (params, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findConfirmAmountReceivedOfCustomer(
        params.id,
        {
          take: params.take,
          page: params.page,
        }
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findForReceivedMoneyTabList = createAsyncThunk(
  'orderItem/findForReceivedMoneyTabList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findForReceivedMoney(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findForDebtMoneyTabList = createAsyncThunk(
  'orderItem/findForDebtMoneyTabList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findForDebtMoney(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderItemsForForward = createAsyncThunk(
  'orderItem/findOrderItemsForForward',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findOrderItemsForForward(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderItemsWithOrder = createAsyncThunk(
  'orderItem/findOrderItemsWithOrder',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findOrderItemsWithOrder(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findAllOrderItemsWithOrder = createAsyncThunk(
  'orderItem/findAllOrderItemsWithOrder',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findAllOrderItemsWithOrder(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderItem = createAsyncThunk(
  'orderItem/updateOrderItem',
  async (params: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderItemApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderItemSupplierQuantity = createAsyncThunk(
  'orderItem/updateOrderItemSupplierQuantity',
  async (
    {
      id,
      options,
      body,
    }: { id: string; options: any; body: UpdateOrderItemSupplierQuantityBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderItemApi.updateSupplierQuantity(
        id,
        options,
        body
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderItemNote = createAsyncThunk(
  'orderItem/updateOrderItemNote',
  async (params: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderItemApi.updateOrderItemNote(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePreProcessingOrderItem = createAsyncThunk(
  'orderItem/updatePreProcessingOrderItem',
  async (params: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderItemApi.updatePreProcessing(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderItemsFromChainSuppliers = createAsyncThunk(
  'orderItem/findOrderItemsFromChainSuppliers',
  async (query: FindOrderItemsFromChainSuppliersQuery, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.findOrderItemsFromChainSuppliers(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderItemAmountDelivered = createAsyncThunk(
  'orderItem/updateOrderItemAmountDelivered',
  async (params: UpdateAmountDeliveredBody, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.updateAmountDelivered(params);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderItemAmountDeliveredReceived = createAsyncThunk(
  'orderItem/updateOrderItemAmountDeliveredReceived',
  async (params: UpdateAmountDeliveredReceivedBody, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.updateAmountDeliveredReceived(params);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderItemAmountReceived = createAsyncThunk(
  'orderItem/updateOrderItemAmountReceived',
  async (params: UpdateAmountReceivedBody, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.updateAmountReceived(params);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const convertOrderItemQuantityToStandardUnitByEntity = createAsyncThunk(
  'orderItem/convertOrderItemQuantityToStandardUnitByEntity',
  async ({ orderID }: { orderID: string }, { rejectWithValue }) => {
    try {
      const response = await OrderItemApi.convertQuantityToStandardUnitByEntity(
        orderID
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

interface State {
  list: Array<any>;
  receivedMoneyTabList: Array<any>;
  debtMoneyTabList: Array<any>;
}

const initialState: State = {
  list: [],
  receivedMoneyTabList: [],
  debtMoneyTabList: [],
};

const orderItemSlice = createSlice({
  name: 'orderItem',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(findForReceivedMoneyTabList.fulfilled, (state, action) => {
        const { data } = action.payload;
        const newData = data.map(item => {
          return {
            ...item,
            quantityBox: {
              customerReceivedQuantity: item.customerReceivedQuantity,
              unit: {
                unitPrefix: item.product.unitPrefix,
                unitWeight: item.product.unitWeight,
                unitPostfix: item.product.unitPostfix,
              },
            },
          };
        });
        state.receivedMoneyTabList = newData;
      })
      .addCase(findForDebtMoneyTabList.fulfilled, (state, action) => {
        const { data } = action.payload;
        const newData = data.map(item => {
          return {
            ...item,
            quantityBox: {
              customerReceivedQuantity: item.customerReceivedQuantity,
              unit: {
                unitPrefix: item.product.unitPrefix,
                unitWeight: item.product.unitWeight,
                unitPostfix: item.product.unitPostfix,
              },
            },
          };
        });
        state.debtMoneyTabList = newData;
      });
  },
});

const { reducer: orderItemReducer } = orderItemSlice;

export default orderItemReducer;
